import { ArrowStatefulIcon, PlusCircleIcon } from '@mm/icons';
import cloneIcon from 'assets/icons/copy.svg';
import pencilIcon from 'assets/icons/edit.svg';
import trashIcon from 'assets/icons/trash.svg';
import classnames from 'classnames';
import { isBoolean } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import { Button, Card, CardBody, CardHeader } from 'reactstrap';
import DropdownMenu from '../../../../../common/components/DropdownMenu/DropdownMenu';
import { DRGBL_PAGE } from '../../../../../common/constants/draggables';
import PageGroupModel from '@mm/state/model/PageGroupModel';
import PageModel from '@mm/state/model/PageModel';
import {
  mainMenuSelector,
  selectActiveChatbotBuildPageId,
} from '@mm/state/selectors/viewSelectors';
import { colors } from '../../../beta/styles/colors';
import PageInfoEditor from '../PageInfoEditor/PageInfoEditor';
import '../PageOverviewPane/PageOverviewPane.scss';
import './PageGroup.scss';

export const defaultPage = (page: PageModel) => {
  return page.kind === 'sms_reply_page' || page.kind === 'otn_page';
};

type Props = {
  onCreatePage: (page: PageModel) => Promise<void>;
  onClonePage: (pageId: number) => void;
  onDeletePage: (pageId: number) => void;
  onDeletePageGroup: (pageGroupId: number, groupName?: string) => void;
  onSelectPage: (pageId: number) => void;
  draggedPageId: number;
  entity: PageGroupModel;
  showed: boolean;
  showEditModal: (groupId?: number) => void;
  defaultGroup: boolean;
  isDragging: boolean;
  pageGroupPages: PageModel[];
};

export const PageGroup = (props: Props) => {
  const {
    showEditModal: showEditModalPassed,
    onCreatePage,
    onClonePage,
    onDeletePage,
    onSelectPage,
    showed,
    onDeletePageGroup,
    entity,
    isDragging,
    pageGroupPages,
    draggedPageId,
    defaultGroup,
  } = props;

  const activePageId = useSelector(selectActiveChatbotBuildPageId);
  const mainMenuSelected = useSelector(mainMenuSelector);

  const [showGroupCard, setShowGroupCard] = useState(showed);
  const [pageInfoModal, setPageInfoModal] = useState(false);
  const [dragging, setDragging] = useState(false);

  const onPageClick = (pageId: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSelectPage(pageId);
  };

  const setCardToogle = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setShowGroupCard(!showGroupCard);
  };

  const togglePageModal = () => {
    setPageInfoModal(!pageInfoModal);
  };

  const showEditModal = (e: React.MouseEvent, groupId: number) => {
    e.stopPropagation();
    showEditModalPassed(groupId);
  };

  useEffect(() => {
    if (isBoolean(showed) && showed !== showGroupCard) {
      setShowGroupCard(showed);
    }
  }, [showed]);

  useEffect(() => {
    if (isDragging !== dragging) {
      setDragging(isDragging);
    }
  }, [isDragging]);

  const renderGroups = () => {
    return (
      <CardBody className="page-overview__default-pages-container">
        <ul>
          <div className="page-group__add-page--link-container">
            <Button
              className="page-info-icon btn-md page-group__btn page-group__btn--remove page-group__btn-page"
              onClick={togglePageModal}
            >
              <PlusCircleIcon
                className="page-group__pages-icon"
                color={colors.accentBlue}
              />
              <span className="page-group__add-page--text">Add Dialogue</span>
            </Button>
          </div>

          {pageGroupPages.map((page, index) => {
            const selected = page.id === activePageId && !mainMenuSelected;
            const dragged =
              page.id !== activePageId &&
              page.id === draggedPageId &&
              !mainMenuSelected;
            const itemClassName = classnames(
              'page-group__special-page',
              {
                'page-group__special-page--selected': selected,
              },
              {
                'page-group__special-page--dragged': dragged,
              },
            );

            const itemTextClassName = classnames('page-group__text', {
              'page-group__text--selected': selected,
            });

            const menuItems = [
              {
                icon: cloneIcon,
                name: 'Clone Dialogue',
                onClick: (e: React.MouseEvent) => {
                  e.stopPropagation();
                  onClonePage(page.id);
                },
              },
            ];
            if (!defaultGroup || !defaultPage(page)) {
              menuItems.push({
                icon: trashIcon,
                name: 'Delete Dialogue',
                onClick: (e: React.MouseEvent) => {
                  e.stopPropagation();
                  onDeletePage(page.id);
                },
              });
            }

            return (
              <Draggable
                draggableId={`draggable-pageId-${page.id}`}
                index={index}
                type={DRGBL_PAGE}
                key={page.id}
              >
                {provided => (
                  <li
                    ref={provided.innerRef}
                    className={itemClassName}
                    onClick={(e: React.MouseEvent) => onPageClick(page.id, e)}
                    key={index}
                    data-bot-builder-page-id={page.id}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                  >
                    <div className="page-group__page-info">
                      <span className={itemTextClassName}>{page.name}</span>
                      <DropdownMenu menuItems={menuItems} snowmanMenu />
                    </div>
                  </li>
                )}
              </Draggable>
            );
          })}
        </ul>
      </CardBody>
    );
  };

  const shouldShowPages = !dragging && showGroupCard;
  const arrowDirection = shouldShowPages ? 'down' : 'right';

  const menuItems = [
    {
      icon: pencilIcon,
      name: 'Edit Folder',
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        showEditModal(e, entity.id);
      },
    },
    {
      icon: trashIcon,
      name: 'Delete Folder',
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        onDeletePageGroup(entity.id, entity.name);
      },
    },
  ];
  const groupNameClass = classnames('page-overview__group-name', {
    'page-overview__group-name--selected': shouldShowPages,
  });
  return (
    <Card className="page-overview__card">
      <CardHeader
        className="page-group__card-header"
        onClick={(e: React.MouseEvent<HTMLElement>) => setCardToogle(e)}
        onMouseDown={() => setDragging(true)}
        onMouseUp={() => setDragging(false)}
        onMouseOver={() => {}}
      >
        <div className={groupNameClass}>
          <div>
            <ArrowStatefulIcon
              direction={arrowDirection}
              size={12}
              className="page-overview__group-arrow-icon"
            />
          </div>
          <div className="page-group__name">{entity.name}</div>
        </div>
        {!defaultGroup && (
          <div className="page-overview__group-buttons">
            <DropdownMenu menuItems={menuItems} snowmanMenu />
          </div>
        )}
      </CardHeader>
      {shouldShowPages && renderGroups()}
      <PageInfoEditor
        closeModal={togglePageModal}
        onCreatePage={onCreatePage}
        showModal={pageInfoModal}
        pageGroupId={entity.id}
      />
    </Card>
  );
};
